import React, { useEffect, useState } from 'react';
import GetData from '../../services/GetData';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { currencyFormatter } from '../Helpers/CurrencyFormat';
import EditIcon from '@mui/icons-material/Edit';
import PostFulfilledInvoice from '../../services/PostFulfilledInvoice';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PostOpenPdf from '../../services/PostOpenPdf';
import { dataGridSx } from '../ui/dataGridStyles';
import { tokens } from '../../theme';

const apiUrl = 'api/invoicedetail';

function lineItemsFromInvoice(invoice) {
  if (!invoice) return [];
  if (Array.isArray(invoice.ingredients) && invoice.ingredients.length) return invoice.ingredients;
  if (Array.isArray(invoice.items) && invoice.items.length) return invoice.items;
  return [];
}

function normalizeLine(e) {
  const name = e.ingredient || e.item || '—';
  const quantity = Number(e.quantity) || 0;
  const total = Number(e.total) || 0;
  const recipeUoM = e.recipeUoM || e.measurement || '';
  const packQuantity = Number(e.packQuantity) || 0;
  return {
    Ingredient: name,
    ParQuantity: packQuantity
      ? `${packQuantity} ${packQuantity > 1 ? 'units' : 'unit'}`
      : '—',
    Quantity: recipeUoM ? `${quantity.toFixed(0)} ${recipeUoM}` : `${quantity.toFixed(0)}`,
    UnitCost: quantity ? total / quantity : 0,
    ItemCost: total,
  };
}

const COLUMNS = [
  { field: 'Ingredient', headerName: 'Ingredient', flex: 2, minWidth: 120 },
  { field: 'ParQuantity', headerName: 'Qty', flex: 0.7, minWidth: 72 },
  { field: 'Quantity', headerName: 'Recipe qty', flex: 0.9, minWidth: 90 },
  {
    field: 'UnitCost',
    headerName: 'Unit cost',
    flex: 0.8,
    minWidth: 88,
    valueFormatter: ({ value }) => currencyFormatter(value || 0, 2),
  },
  {
    field: 'ItemCost',
    headerName: 'Line total',
    flex: 0.8,
    minWidth: 88,
    valueFormatter: ({ value }) => currencyFormatter(value || 0, 2),
  },
];

export default function InvoiceDetails({ invnum, invVendor, close, vendorStock, pdfUpload }) {
  const [load, setLoad] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [row, setRow] = useState([]);
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  const [openIframe, setOpenIFrame] = useState(null);
  const [confirmingUpload, setConfirmingUpload] = useState(false);

  useEffect(() => {
    if (invnum == null || invVendor == null || invVendor === '') {
      setLoad(false);
      setErrorMsg('Missing invoice or vendor.');
      return;
    }
    fetchInvoice();
  }, [invnum, invVendor]);

  const fetchInvoice = async () => {
    setLoad(true);
    setErrorMsg('');
    try {
      const { getData, error } = await GetData(
        apiUrl,
        { invnum, vendorId: invVendor },
        null,
        null,
        null,
      );

      if (error || !getData) {
        setErrorMsg('Could not load invoice details.');
        setRow([]);
        setInvoiceMeta(null);
        return;
      }

      setInvoiceMeta(getData);
      setRow(
        lineItemsFromInvoice(getData).map((e, ind) => ({
          id: ind,
          ...normalizeLine(e),
        })),
      );
    } catch (err) {
      console.log(err);
      setErrorMsg('Could not load invoice details.');
      setRow([]);
      setInvoiceMeta(null);
    } finally {
      setLoad(false);
    }
  };

  const closePop = () => close(false, null);

  const reInvoice = (val) => {
    if (val === 'edit') {
      close('openInvoice', { invNum: invnum, invVend: invVendor }, null, true);
    } else {
      close('openInvoice', invnum, invVendor, false);
    }
  };

  const pdfUploadConfirm = async () => {
    setConfirmingUpload(true);
    const vendorInfo = {
      vendorNm: null,
      vendorId: invVendor,
      orderNumber: invnum,
      invoiceNum: null,
    };
    const { success: invFulfilled, retIngredients } = await PostFulfilledInvoice(
      vendorInfo,
      'api/fulfill-invoice/upload',
    );
    setConfirmingUpload(false);
    if (invFulfilled && retIngredients?.length > 0) {
      close('uploadNewIngs', retIngredients);
    } else {
      close('uploadNewIngs', false);
    }
  };

  const openInvoicePdf = async () => {
    const { pdfUrl } = await PostOpenPdf(invnum, 'api/upload-invoice/retrieve');
    if (pdfUrl) setOpenIFrame(pdfUrl);
  };

  const lineSubtotal = row.reduce((sum, r) => sum + (Number(r.ItemCost) || 0), 0);
  const discount = Number(invoiceMeta?.discount) || 0;
  const tax = Number(invoiceMeta?.tax) || 0;
  const shipping = Number(invoiceMeta?.shipping) || 0;
  const grandTotal = Number(invoiceMeta?.totalPrice) || (lineSubtotal + tax + shipping - discount);

  const showTotals = !vendorStock && !load && !confirmingUpload && !errorMsg && !openIframe;

  if (load) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (openIframe) {
    return (
      <Box sx={{ width: '100%', px: 2.5, pt: 2, pb: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography
            sx={{
              fontFamily: "'Fraunces', 'Figtree', serif",
              fontWeight: 650,
              fontSize: 18,
              lineHeight: 1.25,
            }}
          >
            Invoice PDF
          </Typography>
          <IconButton
            onClick={() => setOpenIFrame(null)}
            aria-label="Close PDF"
            size="small"
            sx={{
              mt: -0.25,
              color: tokens.inkMuted,
              width: 32,
              height: 32,
              borderRadius: 1.5,
              '&:hover': { color: tokens.ink, backgroundColor: tokens.bg },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box
          component="iframe"
          title="Invoice PDF"
          src={openIframe}
          sx={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 360,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      {/* Compact header: title left, close right — same baseline */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 2.5,
          pt: 2,
          pb: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: "'Fraunces', 'Figtree', serif",
              fontWeight: 650,
              fontSize: 20,
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
              color: tokens.ink,
            }}
          >
            {vendorStock ? 'Email order details' : 'Invoice details'}
          </Typography>
          {(invoiceMeta?.invoiceNum || invoiceMeta?.vendor) && (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 13,
                color: tokens.inkMuted,
                lineHeight: 1.3,
              }}
            >
              {[invoiceMeta?.vendor, invoiceMeta?.invoiceNum && `#${invoiceMeta.invoiceNum}`]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={closePop}
          aria-label="Close dialog"
          size="small"
          sx={{
            mt: -0.25,
            color: tokens.inkMuted,
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 1.5,
            '&:hover': {
              color: tokens.ink,
              backgroundColor: tokens.bg,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!vendorStock && !confirmingUpload && !errorMsg && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            flexShrink: 0,
            px: 2.5,
            pt: 1.25,
            pb: 1.25,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          {pdfUpload ? (
            <>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => reInvoice('edit')}>
                Edit
              </Button>
              <Button size="small" variant="contained" onClick={pdfUploadConfirm}>
                Confirm
              </Button>
            </>
          ) : (
            <>
              <Button size="small" variant="text" startIcon={<OpenInNewOutlinedIcon />} onClick={openInvoicePdf}>
                Open PDF
              </Button>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => reInvoice('edit')}>
                Edit
              </Button>
              <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => reInvoice('re-inv')}>
                Re-invoice
              </Button>
            </>
          )}
        </Stack>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          px: 2.5,
          pt: 1.25,
          pb: showTotals ? 1 : 2.5,
        }}
      >
        {confirmingUpload ? (
          <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, py: 5 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1.5, color: tokens.inkMuted, fontSize: 14 }}>Confirming invoice…</Typography>
          </Box>
        ) : errorMsg ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography sx={{ color: tokens.danger, mb: 1.5 }}>{errorMsg}</Typography>
            <Button variant="outlined" onClick={fetchInvoice}>Retry</Button>
          </Box>
        ) : (
          <DataGrid
            rows={row}
            columns={COLUMNS}
            autoHeight
            rowHeight={44}
            columnHeaderHeight={48}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: 'No line items on this invoice' }}
            sx={{
              ...dataGridSx,
              border: 'none',
              minHeight: 160,
              '& .MuiDataGrid-virtualScroller': { overflow: 'hidden !important' },
              '& .MuiDataGrid-main': { overflow: 'hidden' },
            }}
          />
        )}
      </Box>

      {showTotals && (
        <Box
          component="footer"
          sx={{
            flexShrink: 0,
            mt: 'auto',
            px: 2.5,
            pt: 2,
            pb: 2.25,
            borderTop: `1px solid ${tokens.border}`,
            backgroundColor: tokens.bg,
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: { xs: 2, sm: 3 },
            rowGap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          {[
            ['Subtotal', lineSubtotal, false],
            ['Discount', discount, false],
            ['Shipping', shipping, false],
            ['Tax', tax, false],
            ['Total', grandTotal, true],
          ].map(([label, amount, emphasize]) => (
            <Box key={label} sx={{ minWidth: emphasize ? 104 : 80 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: tokens.inkMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  lineHeight: 1.4,
                  mb: 0.35,
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: emphasize ? 700 : 650,
                  fontSize: emphasize ? 17 : 15,
                  color: emphasize ? tokens.accent : tokens.ink,
                  lineHeight: 1.35,
                }}
              >
                {currencyFormatter(amount, 2)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
